import React, { useState } from 'react';
import { ArrowUpRight, Clock, BookOpen, X, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { articles, profile } from '../mock';

const Blog = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const openArticle = (a) => {
    setActive(a);
    setOpen(true);
  };

  return (
    <section id="blog" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-mark" />
              <span className="eyebrow">Artículos de interés</span>
            </div>
            <h2 className="font-serif text-[40px] lg:text-[58px] leading-[1.02] text-[#2E2A23] tracking-[-0.02em] max-w-[680px]">
              Reflexiones desde
              <span className="italic text-[#B8806E]"> el consultorio.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-[13px] tracking-[0.18em] uppercase text-[#8A7E6E]">
            <BookOpen size={16} strokeWidth={1.5} />
            Hacé clic para leer
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {articles.map((a, i) => (
            <button
              key={a.id}
              onClick={() => openArticle(a)}
              className="group relative bg-[#FAF4E8] border border-[#DBCEB4] rounded-[20px] overflow-hidden hover:shadow-[0_25px_60px_-30px_rgba(46,42,35,0.4)] hover:border-[#B8806E] transition-all duration-500 text-left"
            >
              <div className="relative overflow-hidden h-[300px]">
                <img
                  src={a.image}
                  alt={a.title}
                  className="w-full h-full object-cover img-hover"
                />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#FAF4E8]/95 text-[#B8806E] text-[11px] tracking-[0.18em] uppercase">
                  {a.category}
                </div>
              </div>

              <div className="p-7 lg:p-8">
                <div className="flex items-center gap-4 text-[12px] text-[#8A7E6E] mb-4">
                  <span>{a.date}</span>
                  <span className="w-1 h-1 rounded-full bg-[#C5B496]" />
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} strokeWidth={1.6} />
                    {a.readTime} de lectura
                  </span>
                </div>
                <h3 className="font-serif text-[#2E2A23] leading-[1.15] text-[26px] lg:text-[30px]">
                  {a.title}
                </h3>
                <p className="mt-3 text-[14.5px] leading-[1.7] text-[#2E2A23]/75">
                  {a.excerpt}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#B8806E] text-[13px] tracking-[0.16em] uppercase group-hover:gap-3 transition-all duration-300">
                  Leer artículo
                  <ArrowUpRight size={16} strokeWidth={1.6} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Article Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[860px] w-[94vw] max-h-[90vh] overflow-y-auto bg-[#F4EDE2] border-[#DBCEB4] p-0 rounded-[20px] [&>button]:hidden">
          {active && (
            <article>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>

              <div className="relative h-[260px] sm:h-[340px] overflow-hidden rounded-t-[20px]">
                <img
                  src={active.image}
                  alt={active.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F1B16]/55 via-transparent to-[#1F1B16]/15" />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar"
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#F4EDE2]/95 grid place-items-center text-[#2E2A23] hover:bg-[#F4EDE2] transition-colors"
                >
                  <X size={18} strokeWidth={1.6} />
                </button>
                <div className="absolute bottom-5 left-6 right-6">
                  <span className="inline-block px-3 py-1.5 rounded-full bg-[#FAF4E8]/95 text-[#B8806E] text-[11px] tracking-[0.18em] uppercase mb-3">
                    {active.category}
                  </span>
                  <h2 className="font-serif text-[#FAF4E8] text-[28px] sm:text-[36px] leading-[1.1] tracking-[-0.01em]">
                    {active.title}
                  </h2>
                </div>
              </div>

              <div className="p-7 sm:p-10">
                <div className="flex items-center gap-4 text-[12.5px] text-[#8A7E6E] mb-7">
                  <span>{active.date}</span>
                  <span className="w-1 h-1 rounded-full bg-[#C5B496]" />
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} strokeWidth={1.6} />
                    {active.readTime} de lectura
                  </span>
                  <span className="w-1 h-1 rounded-full bg-[#C5B496]" />
                  <span>Por Karina Negrín</span>
                </div>

                <p className="font-serif italic text-[20px] sm:text-[22px] leading-[1.5] text-[#B8806E] border-l-2 border-[#B8806E] pl-5">
                  {active.excerpt}
                </p>

                <div className="mt-8 space-y-5 text-[16px] leading-[1.85] text-[#2E2A23]/85 font-light">
                  {active.sections?.map((s, i) => {
                    if (s.type === 'h') {
                      return (
                        <h3
                          key={i}
                          className="font-serif text-[24px] sm:text-[28px] leading-[1.2] text-[#2E2A23] tracking-[-0.01em] pt-4"
                        >
                          {s.text}
                        </h3>
                      );
                    }
                    if (s.type === 'list') {
                      return (
                        <ul key={i} className="space-y-2.5 pl-1">
                          {s.items.map((it, j) => (
                            <li key={j} className="flex items-start gap-3">
                              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#B8806E] shrink-0" />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    if (s.type === 'sign') {
                      return (
                        <p
                          key={i}
                          className="font-script text-[#B8806E] text-[28px] leading-tight whitespace-pre-line pt-3"
                        >
                          {s.text}
                        </p>
                      );
                    }
                    return <p key={i}>{s.text}</p>;
                  })}
                  {active.content?.map((p, i) => (
                    <p key={`legacy-${i}`}>{p}</p>
                  ))}
                </div>

                <div className="mt-10 pt-8 border-t border-[#DBCEB4] flex items-center justify-between flex-wrap gap-4">
                  <p className="text-[13px] text-[#8A7E6E] max-w-[420px] leading-[1.6]">
                    ¿Te resonó algo? Escribime y conversamos. Cada proceso es
                    único.
                  </p>
                  <a
                    href={profile.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary"
                  >
                    <MessageCircle size={16} strokeWidth={1.6} />
                    Escribime
                  </a>
                </div>
              </div>
            </article>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Blog;
